import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  Autocomplete,
  Chip,
  FormHelperText,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon
} from '@mui/icons-material';
import apiService from '../../../services/apiService';
import { useVoucherTemplatePermissions } from '../../../hooks/useVoucherTemplatePermissions';

interface Account {
  id: string;
  accountNumber: string;
  name: string;
}

interface VoucherTemplate {
  id?: string;
  name: string;
  description?: string;
  templateType: 'receipt' | 'payment' | 'journal';
  debitAccount?: Account | null;
  creditAccount?: Account | null;
  debitAccounts?: Account[];
  creditAccounts?: Account[];
}

interface VoucherTemplateModalProps {
  open: boolean;
  onClose: () => void;
  onSave?: () => void;
}

const VoucherTemplateModal: React.FC<VoucherTemplateModalProps> = ({ open, onClose, onSave }) => {
  // استخدام hook الصلاحيات
  const {
    canAccessVoucherTemplates,
    canCreateVoucherTemplate,
    canEditVoucherTemplate,
    canDeleteVoucherTemplate
  } = useVoucherTemplatePermissions();

  const [templates, setTemplates] = useState<VoucherTemplate[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingTemplate, setEditingTemplate] = useState<VoucherTemplate | null>(null);
  const [showForm, setShowForm] = useState(false);

  // بيانات النموذج
  const [formData, setFormData] = useState<VoucherTemplate>({
    name: '',
    description: '',
    templateType: 'receipt',
    debitAccount: null,
    creditAccount: null,
    debitAccounts: [],
    creditAccounts: []
  });

  const [formErrors, setFormErrors] = useState<{
    name?: string;
    debitAccount?: string;
    creditAccount?: string;
    debitAccounts?: string;
    creditAccounts?: string;
  }>({});

  // تحميل البيانات عند فتح النافذة
  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open]);

  const loadData = async () => {
    // التحقق من صلاحية الوصول
    if (!canAccessVoucherTemplates()) {
      setError('ليس لديك صلاحية الوصول إلى قوالب السندات');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const [templatesData, accountsData] = await Promise.all([
        apiService.get<VoucherTemplate[]>('/finance/voucher-templates'),
        apiService.get<Account[]>('/finance/accounts')
      ]);
      
      setTemplates(templatesData);
      setAccounts(accountsData.filter((acc: any) => acc.isActive));
    } catch (err: any) {
      console.error('خطأ في تحميل البيانات:', err);
      setError(err.response?.data?.message || 'حدث خطأ في تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field as keyof typeof formErrors]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field as keyof typeof formErrors];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const errors: typeof formErrors = {};

    if (!formData.name.trim()) {
      errors.name = 'اسم القالب مطلوب';
    }

    if (formData.templateType === 'receipt' || formData.templateType === 'payment') {
      if (!formData.debitAccount) {
        errors.debitAccount = 'حساب المدين مطلوب';
      }
      if (!formData.creditAccount) {
        errors.creditAccount = 'حساب الدائن مطلوب';
      }
    } else if (formData.templateType === 'journal') {
      if (!formData.debitAccounts || formData.debitAccounts.length === 0) {
        errors.debitAccounts = 'يجب اختيار حساب مدين واحد على الأقل';
      }
      if (!formData.creditAccounts || formData.creditAccounts.length === 0) {
        errors.creditAccounts = 'يجب اختيار حساب دائن واحد على الأقل';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    // التحقق من الصلاحيات
    if (editingTemplate?.id && !canEditVoucherTemplate()) {
      setError('ليس لديك صلاحية تعديل قوالب السندات');
      return;
    }
    if (!editingTemplate?.id && !canCreateVoucherTemplate()) {
      setError('ليس لديك صلاحية إنشاء قوالب السندات');
      return;
    }

    if (!validateForm()) return;

    setSaving(true);
    setError(null);

    try {
      const payload: any = {
        name: formData.name,
        description: formData.description,
        templateType: formData.templateType
      };

      if (formData.templateType === 'receipt' || formData.templateType === 'payment') {
        payload.debitAccount = formData.debitAccount?.id;
        payload.creditAccount = formData.creditAccount?.id;
      } else {
        payload.debitAccounts = formData.debitAccounts?.map(acc => acc.id) || [];
        payload.creditAccounts = formData.creditAccounts?.map(acc => acc.id) || [];
      }

      if (editingTemplate?.id) {
        await apiService.put(`/finance/voucher-templates/${editingTemplate.id}`, payload);
      } else {
        await apiService.post('/finance/voucher-templates', payload);
      }

      await loadData();
      resetForm();
      if (onSave) onSave();
    } catch (err: any) {
      console.error('خطأ في حفظ القالب:', err);
      setError(err.response?.data?.message || 'حدث خطأ في حفظ القالب');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (template: VoucherTemplate) => {
    // التحقق من صلاحية التعديل
    if (!canEditVoucherTemplate()) {
      setError('ليس لديك صلاحية تعديل قوالب السندات');
      return;
    }

    setEditingTemplate(template);
    setFormData({
      name: template.name,
      description: template.description || '',
      templateType: template.templateType,
      debitAccount: template.debitAccount || null,
      creditAccount: template.creditAccount || null,
      debitAccounts: template.debitAccounts || [],
      creditAccounts: template.creditAccounts || []
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    // التحقق من صلاحية الحذف
    if (!canDeleteVoucherTemplate()) {
      setError('ليس لديك صلاحية حذف قوالب السندات');
      return;
    }

    if (!window.confirm('هل أنت متأكد من حذف هذا القالب؟')) return;

    try {
      await apiService.delete(`/finance/voucher-templates/${id}`);
      await loadData();
      if (onSave) onSave();
    } catch (err: any) {
      console.error('خطأ في حذف القالب:', err);
      setError(err.response?.data?.message || 'حدث خطأ في حذف القالب');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      templateType: 'receipt',
      debitAccount: null,
      creditAccount: null,
      debitAccounts: [],
      creditAccounts: []
    });
    setFormErrors({});
    setEditingTemplate(null);
    setShowForm(false);
  };

  const getTemplateTypeLabel = (type: string) => {
    switch (type) {
      case 'receipt': return 'سند قبض';
      case 'payment': return 'سند صرف';
      case 'journal': return 'قيد يومية';
      default: return type;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">قوالب السندات المحفوظة</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : showForm ? (
          <Box>
            <Typography variant="subtitle1" gutterBottom fontWeight={600}>
              {editingTemplate ? 'تعديل القالب' : 'إضافة قالب جديد'}
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                label="اسم القالب"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                error={!!formErrors.name}
                helperText={formErrors.name}
                required
              />

              <TextField
                fullWidth
                label="الوصف (اختياري)"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                multiline
                rows={2}
              />

              <FormControl fullWidth required>
                <InputLabel>نوع القالب</InputLabel>
                <Select
                  value={formData.templateType}
                  label="نوع القالب"
                  onChange={(e) => handleInputChange('templateType', e.target.value)}
                >
                  <MenuItem value="receipt">سند قبض</MenuItem>
                  <MenuItem value="payment">سند صرف</MenuItem>
                  <MenuItem value="journal">قيد يومية</MenuItem>
                </Select>
              </FormControl>

              {(formData.templateType === 'receipt' || formData.templateType === 'payment') ? (
                <>
                  <Autocomplete
                    options={accounts}
                    getOptionLabel={(option) => `${option.accountNumber} - ${option.name}`}
                    value={formData.debitAccount}
                    onChange={(_, value) => handleInputChange('debitAccount', value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="حساب المدين"
                        required
                        error={!!formErrors.debitAccount}
                        helperText={formErrors.debitAccount}
                      />
                    )}
                  />

                  <Autocomplete
                    options={accounts}
                    getOptionLabel={(option) => `${option.accountNumber} - ${option.name}`}
                    value={formData.creditAccount}
                    onChange={(_, value) => handleInputChange('creditAccount', value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="حساب الدائن"
                        required
                        error={!!formErrors.creditAccount}
                        helperText={formErrors.creditAccount}
                      />
                    )}
                  />
                </>
              ) : (
                <>
                  <Autocomplete
                    multiple
                    options={accounts}
                    getOptionLabel={(option) => `${option.accountNumber} - ${option.name}`}
                    value={formData.debitAccounts || []}
                    onChange={(_, value) => handleInputChange('debitAccounts', value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="الحسابات المدينة"
                        required
                        error={!!formErrors.debitAccounts}
                        helperText={formErrors.debitAccounts || 'يمكن اختيار أكثر من حساب'}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={`${option.accountNumber} - ${option.name}`}
                          {...getTagProps({ index })}
                          size="small"
                        />
                      ))
                    }
                  />

                  <Autocomplete
                    multiple
                    options={accounts}
                    getOptionLabel={(option) => `${option.accountNumber} - ${option.name}`}
                    value={formData.creditAccounts || []}
                    onChange={(_, value) => handleInputChange('creditAccounts', value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="الحسابات الدائنة"
                        required
                        error={!!formErrors.creditAccounts}
                        helperText={formErrors.creditAccounts || 'يمكن اختيار أكثر من حساب'}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={`${option.accountNumber} - ${option.name}`}
                          {...getTagProps({ index })}
                          size="small"
                        />
                      ))
                    }
                  />
                </>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving}
              >
                حفظ
              </Button>
              <Button variant="outlined" onClick={resetForm}>
                إلغاء
              </Button>
            </Box>
          </Box>
        ) : (
          <Box>
            {canCreateVoucherTemplate() && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setShowForm(true)}
                sx={{ mb: 2 }}
              >
                إضافة قالب جديد
              </Button>
            )}

            {templates.length === 0 ? (
              <Typography color="text.secondary" align="center" py={4}>
                لا توجد قوالب محفوظة
              </Typography>
            ) : (
              <List>
                {templates.map((template) => (
                  <ListItem
                    key={template.id}
                    sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 1
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {template.name}
                          </Typography>
                          <Chip
                            label={getTemplateTypeLabel(template.templateType)}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          {template.description && (
                            <Typography variant="body2" color="text.secondary">
                              {template.description}
                            </Typography>
                          )}
                          {template.templateType === 'journal' ? (
                            <Box mt={1}>
                              <Typography variant="caption" display="block">
                                المدين: {template.debitAccounts?.map(acc => acc.name).join(', ')}
                              </Typography>
                              <Typography variant="caption" display="block">
                                الدائن: {template.creditAccounts?.map(acc => acc.name).join(', ')}
                              </Typography>
                            </Box>
                          ) : (
                            <Box mt={1}>
                              <Typography variant="caption" display="block">
                                المدين: {template.debitAccount?.accountNumber} - {template.debitAccount?.name}
                              </Typography>
                              <Typography variant="caption" display="block">
                                الدائن: {template.creditAccount?.accountNumber} - {template.creditAccount?.name}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      {canEditVoucherTemplate() && (
                        <IconButton
                          edge="end"
                          onClick={() => handleEdit(template)}
                          sx={{ mr: 1 }}
                        >
                          <EditIcon />
                        </IconButton>
                      )}
                      {canDeleteVoucherTemplate() && (
                        <IconButton
                          edge="end"
                          onClick={() => handleDelete(template.id!)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>إغلاق</Button>
      </DialogActions>
    </Dialog>
  );
};

export default VoucherTemplateModal;